import { useEffect, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface AnimatedPlaceholderProps {
  placeholders: string[];
  className?: string;
  typingSpeed?: number;
  deletingSpeed?: number;
  pauseDuration?: number;
}

export function AnimatedPlaceholder({
  placeholders,
  className = '',
  typingSpeed = 50,
  deletingSpeed = 30,
  pauseDuration = 2000,
}: AnimatedPlaceholderProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const currentPlaceholder = placeholders[currentIndex];

  const animatePlaceholder = useCallback(() => {
    if (isPaused) return;

    if (!isDeleting) {
      // Typing
      if (displayedText.length < currentPlaceholder.length) {
        setDisplayedText(currentPlaceholder.slice(0, displayedText.length + 1));
      } else {
        // Finished typing, pause before deleting
        setIsPaused(true);
        setTimeout(() => {
          setIsPaused(false);
          setIsDeleting(true);
        }, pauseDuration);
      }
    } else {
      // Deleting
      if (displayedText.length > 0) {
        setDisplayedText(displayedText.slice(0, -1));
      } else {
        // Finished deleting, move to next placeholder
        setIsDeleting(false);
        setCurrentIndex((prev) => (prev + 1) % placeholders.length);
      }
    }
  }, [currentPlaceholder, displayedText, isDeleting, isPaused, pauseDuration, placeholders.length]);

  useEffect(() => {
    const speed = isDeleting ? deletingSpeed : typingSpeed;
    const timer = setTimeout(animatePlaceholder, isPaused ? pauseDuration : speed);
    return () => clearTimeout(timer);
  }, [animatePlaceholder, deletingSpeed, typingSpeed, isPaused, isDeleting, pauseDuration]);

  return (
    <span className={cn('text-muted-foreground pointer-events-none select-none', className)}>
      {displayedText}
      <span className="animate-pulse ml-0.5 inline-block w-[2px] h-[1.1em] bg-muted-foreground/50 align-middle" />
    </span>
  );
}
